import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  RefreshCw,
  Printer,
  FileText,
  Tag,
  CheckCircle2,
  Clock,
  AlertOctagon,
  ExternalLink,
  Package,
  User,
  Calendar,
  Building2,
  Loader2,
  History
} from 'lucide-react';
import { fetchRampReceptions, fetchRampReceptionByNri } from '../services/oabService';
import { OABStageNotifyPanel } from './OABStageNotifyPanel';

export interface RampReceptionHistoryViewProps {
  initialNriFolio?: string | null;
  initialFolioOAB?: string;
  onReprintEntry: (nriDetail: any) => void;
  onReprintRejection: (nriDetail: any) => void;
  onReprintLabels: (nriDetail: any) => void;
}

export const RampReceptionHistoryView: React.FC<RampReceptionHistoryViewProps> = ({
  initialNriFolio,
  initialFolioOAB,
  onReprintEntry,
  onReprintRejection,
  onReprintLabels
}) => {
  const [receptions, setReceptions] = useState<any[]>([]);
  const [selectedNriFolio, setSelectedNriFolio] = useState<string | null>(initialNriFolio || null);
  const [selectedDetail, setSelectedDetail] = useState<any | null>(null);
  const [isLoadingList, setIsLoadingList] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>(initialFolioOAB || '');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PARCIAL' | 'RECHAZO' | 'COMPLETA'>('ALL');
  const [printedNriMap, setPrintedNriMap] = useState<Record<string, boolean>>({});
  const [printedRejectionMap, setPrintedRejectionMap] = useState<Record<string, boolean>>({});
  const [showPrevCompleted, setShowPrevCompleted] = useState<boolean>(false);
  const [showCanceledMgmt, setShowCanceledMgmt] = useState<boolean>(false);

  const loadList = useCallback(async (refresh = false) => {
    setIsLoadingList(true);
    try {
      const list = await fetchRampReceptions({
        estado: statusFilter,
        q: searchQuery.trim() || undefined,
        refresh
      });
      setReceptions(list);

      if (initialNriFolio && !selectedDetail) {
        setSelectedNriFolio(initialNriFolio);
      } else if (!selectedNriFolio && list.length > 0) {
        setSelectedNriFolio(list[0].nriFolio);
      }
    } finally {
      setIsLoadingList(false);
    }
  }, [statusFilter, searchQuery, initialNriFolio]);

  useEffect(() => {
    loadList(false);
  }, [loadList]);

  useEffect(() => {
    setShowPrevCompleted(false);
    setShowCanceledMgmt(false);
    if (!selectedNriFolio) {
      setSelectedDetail(null);
      return;
    }
    let cancelled = false;
    setIsLoadingDetail(true);
    fetchRampReceptionByNri(selectedNriFolio)
      .then(detail => {
        if (!cancelled) {
          setSelectedDetail(detail);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedNriFolio]);

  const getEstadoBadge = (estado: string) => {
    if (estado === 'Rechazo Total') {
      return 'bg-red-500/20 text-red-300 border-red-500/40';
    }
    if (estado === 'Con Rechazos') {
      return 'bg-rose-500/20 text-rose-300 border-rose-500/40';
    }
    if (estado === 'Recepción Parcial') {
      return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
    }
    return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-page text-slate-200">
      {/* Barra de Filtros y Búsqueda */}
      <div className="p-3 bg-surface border-b border-borderSubtle flex flex-wrap items-center justify-between gap-2.5 text-xs">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <div className="relative flex-1 max-w-md">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por Folio NRI, OAB, Proveedor, Guía o Tienda..."
              className="w-full pl-8 pr-3 py-1.5 bg-page border border-borderSubtle rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-brand-500"
            />
          </div>

          <div className="flex items-center gap-1 bg-page p-1 rounded-lg border border-borderSubtle">
            {[
              { id: 'ALL', label: 'Todas' },
              { id: 'PARCIAL', label: '⏳ Con Backorder' },
              { id: 'RECHAZO', label: '❌ Con Rechazos' },
              { id: 'COMPLETA', label: '✅ Completas' }
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStatusFilter(tab.id as any)}
                className={`px-2.5 py-1 rounded text-[11px] font-semibold transition ${
                  statusFilter === tab.id
                    ? 'bg-brand-500/20 text-brand-300 border border-brand-500/40'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => loadList(true)}
          disabled={isLoadingList}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-semibold transition active:scale-95 disabled:opacity-50"
          title="Sincronizar historial con BD_Recepciones_Rampa en Notion"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-brand-400 ${isLoadingList ? 'animate-spin' : ''}`} />
          <span>Sincronizar Notion</span>
        </button>
      </div>

      {/* Cuerpo Maestro-Detalle */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
        {/* Columna Izquierda: Lista de NRIs */}
        <div className="lg:col-span-4 border-r border-borderSubtle overflow-y-auto p-2.5 space-y-2 bg-page/60">
          {isLoadingList && receptions.length === 0 ? (
            <div className="py-12 text-center text-slate-400 flex flex-col items-center gap-2 text-xs">
              <Loader2 className="w-5 h-5 animate-spin text-brand-400" />
              <span>Consultando historial de recepciones (NRI)...</span>
            </div>
          ) : receptions.length === 0 ? (
            <div className="py-12 px-4 text-center text-slate-400 space-y-2 text-xs">
              <History className="w-8 h-8 text-slate-600 mx-auto" />
              <p className="font-semibold text-slate-300">No hay Notas de Recepción Interna (NRI) registradas con este filtro.</p>
              <p className="text-[11px] text-slate-500">
                Cada recepción confirmada en el muelle genera automáticamente su folio inmutable <code>NRI-YYYYMMDD-XX</code> en Notion (<code>BD_Recepciones_Rampa</code>) y en este historial.
              </p>
            </div>
          ) : (
            receptions.map(entry => {
              const isSelected = selectedNriFolio === entry.nriFolio;
              const tot = entry.totales || {};
              return (
                <button
                  key={entry.nriFolio}
                  type="button"
                  onClick={() => setSelectedNriFolio(entry.nriFolio)}
                  className={`w-full text-left p-3 rounded-xl border transition space-y-1.5 ${
                    isSelected
                      ? 'bg-brand-500/15 border-brand-500/50 shadow-sm'
                      : 'bg-surface hover:bg-surfaceHigh border-borderSubtle'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono font-black text-xs text-emerald-400">
                      {entry.nriFolio}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${getEstadoBadge(entry.estadoRecepcion)}`}>
                      {entry.estadoRecepcion || 'Completada'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-[11px] text-slate-300">
                    <span className="font-mono font-semibold text-sky-300">{entry.folioOAB}</span>
                    <span className="text-slate-400">{entry.fechaRecepcion}</span>
                  </div>

                  <div className="text-[11px] text-slate-300 truncate font-medium">
                    🏢 {entry.proveedor || 'Proveedor General'} · <span className="font-mono text-slate-400">#{entry.numeroNotaEntrega || 'S/N'}</span>
                  </div>

                  {entry.actaRechazoFolio && (
                    <div className="text-[10px] font-mono font-bold text-rose-400 bg-rose-950/40 border border-rose-800/50 px-2 py-0.5 rounded inline-block">
                      ⚠️ Acta: {entry.actaRechazoFolio}
                    </div>
                  )}

                  <div className="pt-1 border-t border-borderSubtle/60 flex items-center justify-between text-[10px] font-mono">
                    <span className="text-emerald-400 font-bold">✅ +{tot.recibidosHoy ?? 0} rec</span>
                    <span className={(tot.pendientesBackorder || 0) > 0 ? 'text-amber-400 font-bold' : 'text-slate-500'}>
                      ⏳ {tot.pendientesBackorder ?? 0} pend
                    </span>
                    <span className={(tot.rechazadosHoy || 0) > 0 ? 'text-rose-400 font-bold' : 'text-slate-500'}>
                      ❌ {tot.rechazadosHoy ?? 0} rech
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Columna Derecha: Ficha Tripartita de la NRI Seleccionada */}
        <div className="lg:col-span-8 overflow-y-auto p-4 space-y-4">
          {isLoadingDetail ? (
            <div className="py-16 text-center text-slate-400 flex flex-col items-center gap-2 text-xs">
              <Loader2 className="w-6 h-6 animate-spin text-brand-400" />
              <span>Cargando expediente tripartito de {selectedNriFolio}...</span>
            </div>
          ) : !selectedDetail ? (
            <div className="py-16 text-center text-slate-500 text-xs">
              Selecciona una Nota de Recepción Interna (NRI) de la lista izquierda para inspeccionar su corte tripartito, reimprimir documentos o enviar notificaciones.
            </div>
          ) : (
            <>
              {/* Cabecera del Expediente NRI */}
              <div className="p-4 rounded-xl bg-surface border border-borderSubtle space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-2.5 py-1 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-mono text-sm font-black">
                        {selectedDetail.nriFolio}
                      </span>
                      <span className="px-2.5 py-1 rounded-md bg-sky-500/15 border border-sky-500/30 text-sky-300 font-mono text-xs font-bold">
                        OAB: {selectedDetail.folioOAB}
                      </span>
                      <span className={`px-2.5 py-1 rounded-md text-xs font-bold border ${getEstadoBadge(selectedDetail.estadoRecepcion)}`}>
                        {selectedDetail.estadoRecepcion}
                      </span>
                      {selectedDetail.actaRechazoFolio && (
                        <span className="px-2.5 py-1 rounded-md bg-rose-500/20 border border-rose-500/40 text-rose-300 font-mono text-xs font-bold">
                          ⚠️ {selectedDetail.actaRechazoFolio}
                        </span>
                      )}
                    </div>
                    {selectedDetail.offlineNriFolio && (
                      <p className="text-[11px] font-mono text-slate-400 mt-1">
                        Alias Offline sincronizado: <code>{selectedDetail.offlineNriFolio}</code>
                      </p>
                    )}
                  </div>

                  {/* Barra de Reimpresión Integral en 1 Clic (Nodo 4/4) */}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setPrintedNriMap(prev => ({ ...prev, [selectedDetail.nriFolio]: true }));
                        onReprintEntry(selectedDetail);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition shadow active:scale-95"
                      title="Reimprimir Nota de Recepción Interna (NRI) en formato Carta"
                    >
                      <Printer className="w-3.5 h-3.5" />
                      <span>Reimprimir NRI (Carta)</span>
                    </button>

                    {(selectedDetail.itemsRechazados?.length || 0) > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setPrintedRejectionMap(prev => ({ ...prev, [selectedDetail.nriFolio]: true }));
                          onReprintRejection(selectedDetail);
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold transition shadow active:scale-95"
                        title="Reimprimir Acta Formal de No Conformidad y Rechazo en formato Carta"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        <span>Reimprimir Acta Rechazo</span>
                      </button>
                    )}

                    {(selectedDetail.itemsRecibidos?.length || 0) > 0 && (
                      <button
                        type="button"
                        onClick={() => onReprintLabels(selectedDetail)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-slate-950 text-xs font-bold transition shadow active:scale-95"
                        title="Reimprimir Etiquetas Térmicas 4-UP de este viaje"
                      >
                        <Tag className="w-3.5 h-3.5" />
                        <span>Reimprimir Etiquetas 4-UP</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Metadatos del Viaje */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-borderSubtle text-xs">
                  <div className="flex items-start gap-2">
                    <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                    <div>
                      <span className="text-[10px] uppercase text-slate-500 block">Proveedor / Guía</span>
                      <span className="font-bold text-slate-200">{selectedDetail.proveedor || 'Proveedor General'}</span>
                      <span className="block font-mono text-[11px] text-cyan-300">
                        {selectedDetail.tipoDocumento || 'NE'}: #{selectedDetail.numeroNotaEntrega || 'S/N'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                    <div>
                      <span className="text-[10px] uppercase text-slate-500 block">Fecha Recepción</span>
                      <span className="font-bold text-slate-200">{selectedDetail.fechaRecepcion}</span>
                      {selectedDetail.choferNombre && (
                        <span className="block text-[11px] text-slate-400">
                          🚚 {selectedDetail.choferNombre} {selectedDetail.placaVehiculo ? `(${selectedDetail.placaVehiculo})` : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <User className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                    <div>
                      <span className="text-[10px] uppercase text-slate-500 block">Receptor Rampa</span>
                      <span className="font-bold text-slate-200">{selectedDetail.receptorNombre || 'Operador Rampa'}</span>
                      <span className="block text-[11px] text-amber-300 truncate max-w-[180px]">
                        🎯 {selectedDetail.tiendaDestino || 'Stock General'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <span className="text-[10px] uppercase text-slate-500 block">Evidencia Fotográfica R2</span>
                    {selectedDetail.comprobanteUrl ? (
                      <a
                        href={selectedDetail.comprobanteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-bold text-brand-300 hover:text-brand-200 underline mt-0.5"
                      >
                        <span>📸 Ver Foto Comprobante</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-[11px] text-slate-500 italic">Sin foto adjunta</span>
                    )}
                  </div>
                </div>

                {/* Cinta de Cuadratura Integral 360° de la OAB — 5 Partes (PLAN-055) */}
                <div className="pt-2.5 border-t border-borderSubtle/70">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] uppercase tracking-wider font-mono font-bold text-slate-400">
                      Cuadratura Integral 360° de la OAB ({selectedDetail.totales?.renglonesTotalesOAB ?? (
                        (selectedDetail.itemsRecibidos?.length || 0) +
                        (selectedDetail.itemsFaltantes?.length || 0) +
                        (selectedDetail.itemsCompletadosPrevios?.length || 0) +
                        (selectedDetail.itemsCanceladosGerencia?.length || 0)
                      )} renglones auditados)
                    </span>
                    {selectedDetail.estadoGeneralOAB && (
                      <span className="text-[10px] font-mono text-slate-400">
                        Estado OAB: <strong className="text-slate-200">{selectedDetail.estadoGeneralOAB}</strong>
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px] font-mono">
                    <div className="p-2 rounded-lg bg-emerald-950/30 border border-emerald-500/30">
                      <div className="text-[10px] text-emerald-300/80">✅ Recibidos Hoy</div>
                      <div className="font-bold text-emerald-400">
                        {selectedDetail.itemsRecibidos?.length || 0} reng. (+{selectedDetail.totales?.recibidosHoy ?? 0}u)
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-amber-950/30 border border-amber-500/30">
                      <div className="text-[10px] text-amber-300/80">⏳ Backorder</div>
                      <div className="font-bold text-amber-400">
                        {selectedDetail.itemsFaltantes?.length || 0} reng. ({selectedDetail.totales?.pendientesBackorder ?? 0}u)
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-rose-950/30 border border-rose-500/30">
                      <div className="text-[10px] text-rose-300/80">❌ Rechazados</div>
                      <div className="font-bold text-rose-400">
                        {selectedDetail.itemsRechazados?.length || 0} reng. ({selectedDetail.totales?.rechazadosHoy ?? 0}u)
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-sky-950/30 border border-sky-500/30">
                      <div className="text-[10px] text-sky-300/80">📦 Fletes Previos</div>
                      <div className="font-bold text-sky-300">
                        {selectedDetail.itemsCompletadosPrevios?.length ?? selectedDetail.totales?.renglonesCompletadosPrevios ?? 0} reng.
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-slate-900/70 border border-slate-700">
                      <div className="text-[10px] text-slate-400">🚫 No Aprobados</div>
                      <div className="font-bold text-slate-300">
                        {selectedDetail.itemsCanceladosGerencia?.length ?? selectedDetail.totales?.renglonesCanceladosGerencia ?? 0} reng.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resumen Tripartito: 3 Tablas */}
              {/* 1. Qué llegó hoy */}
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/10 overflow-hidden">
                <div className="px-3.5 py-2 bg-emerald-950/30 border-b border-emerald-500/20 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 font-bold text-emerald-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>✅ Qué llegó hoy — Mercancía Recibida Conforme ({selectedDetail.itemsRecibidos?.length || 0} renglones)</span>
                  </div>
                  <span className="font-mono font-bold text-emerald-400">
                    Total: +{selectedDetail.totales?.recibidosHoy ?? 0} und ({selectedDetail.totales?.bultosHoy ?? 1} bultos)
                  </span>
                </div>
                {(selectedDetail.itemsRecibidos?.length || 0) === 0 ? (
                  <div className="p-3 text-xs text-slate-400 italic">
                    Sin unidades conformes ingresadas en este viaje.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-borderSubtle text-[10px] uppercase text-slate-400 bg-surface/60">
                          <th className="py-2 px-3">Insumo</th>
                          <th className="py-2 px-2 text-center">Und</th>
                          <th className="py-2 px-2 text-right">Aprobado</th>
                          <th className="py-2 px-2 text-right">Rec. Previo</th>
                          <th className="py-2 px-2 text-right text-emerald-300">Recibido Hoy</th>
                          <th className="py-2 px-2 text-center">Bultos</th>
                          <th className="py-2 px-3">Destino en Planta</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-borderSubtle/50">
                        {selectedDetail.itemsRecibidos.map((it: any, idx: number) => (
                          <tr key={idx} className="hover:bg-surface/40">
                            <td className="py-2 px-3 font-semibold text-slate-100">
                              {it.nombre}
                              {it.codigo && <span className="ml-1.5 font-mono text-[10px] text-slate-400">({it.codigo})</span>}
                            </td>
                            <td className="py-2 px-2 text-center font-mono text-slate-400">{it.unidadMedida || 'und'}</td>
                            <td className="py-2 px-2 text-right font-mono text-slate-400">{it.cantidadAprobada}</td>
                            <td className="py-2 px-2 text-right font-mono text-slate-500">{it.cantidadRecibidaPrevia || 0}</td>
                            <td className="py-2 px-2 text-right font-mono font-bold text-emerald-400">+{it.cantidadRecibidaHoy}</td>
                            <td className="py-2 px-2 text-center font-mono text-slate-300">{it.bultos || 1}</td>
                            <td className="py-2 px-3 text-[11px] text-amber-300 font-medium">{it.proyectoNombre || 'Stock General'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* 2. Qué falta por llegar (Backorder al Corte) */}
              <div className="rounded-xl border border-amber-500/30 bg-amber-950/10 overflow-hidden">
                <div className="px-3.5 py-2 bg-amber-950/30 border-b border-amber-500/20 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 font-bold text-amber-300">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span>⏳ Qué faltaba por llegar al cierre de este corte ({selectedDetail.itemsFaltantes?.length || 0} renglones)</span>
                  </div>
                  <span className="font-mono font-bold text-amber-400">
                    Backorder: {selectedDetail.totales?.pendientesBackorder ?? 0} und
                  </span>
                </div>
                {(selectedDetail.itemsFaltantes?.length || 0) === 0 ? (
                  <div className="p-3 text-xs text-emerald-300 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Orden 100% completa en este corte (0 unidades pendientes).</span>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-borderSubtle text-[10px] uppercase text-slate-400 bg-surface/60">
                          <th className="py-2 px-3">Insumo Pendiente</th>
                          <th className="py-2 px-2 text-center">Und</th>
                          <th className="py-2 px-2 text-right">Aprobado</th>
                          <th className="py-2 px-2 text-right">Acumulado</th>
                          <th className="py-2 px-2 text-right text-amber-300">Faltante</th>
                          <th className="py-2 px-3">Disposición</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-borderSubtle/50">
                        {selectedDetail.itemsFaltantes.map((m: any, idx: number) => (
                          <tr key={idx} className="hover:bg-surface/40">
                            <td className="py-2 px-3 font-semibold text-slate-100">{m.nombre}</td>
                            <td className="py-2 px-2 text-center font-mono text-slate-400">{m.unidadMedida || 'und'}</td>
                            <td className="py-2 px-2 text-right font-mono text-slate-400">{m.cantidadAprobada}</td>
                            <td className="py-2 px-2 text-right font-mono text-slate-300">{m.totalAcumulado}</td>
                            <td className="py-2 px-2 text-right font-mono font-bold text-amber-400">{m.saldoPendiente}</td>
                            <td className="py-2 px-3 text-[11px] text-slate-300">
                              {m.actionOnRemaining === 'REQUOTE_ANOTHER_VENDOR'
                                ? '🔄 Re-cotizar Otro Proveedor'
                                : m.actionOnRemaining === 'DISCARD'
                                ? '🚫 Descartado'
                                : '⏳ Backorder Activo'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* 3. Qué se rechazó / devolvió */}
              <div className="rounded-xl border border-rose-500/30 bg-rose-950/10 overflow-hidden">
                <div className="px-3.5 py-2 bg-rose-950/30 border-b border-rose-500/20 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 font-bold text-rose-300">
                    <AlertOctagon className="w-4 h-4 text-rose-400" />
                    <span>❌ Qué se rechazó / devolvió al transportista ({selectedDetail.itemsRechazados?.length || 0} renglones)</span>
                  </div>
                  <span className="font-mono font-bold text-rose-400">
                    Devuelto: {selectedDetail.totales?.rechazadosHoy ?? 0} und
                  </span>
                </div>
                {(selectedDetail.itemsRechazados?.length || 0) === 0 ? (
                  <div className="p-3 text-xs text-slate-400">
                    0 devoluciones en este viaje.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-borderSubtle text-[10px] uppercase text-slate-400 bg-surface/60">
                          <th className="py-2 px-3">Insumo Devuelto</th>
                          <th className="py-2 px-2 text-center">Und</th>
                          <th className="py-2 px-2 text-right text-rose-300">Cant. Rechazada</th>
                          <th className="py-2 px-3">Motivo Técnico de No Conformidad</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-borderSubtle/50">
                        {selectedDetail.itemsRechazados.map((r: any, idx: number) => (
                          <tr key={idx} className="hover:bg-surface/40">
                            <td className="py-2 px-3 font-semibold text-slate-100">{r.nombre}</td>
                            <td className="py-2 px-2 text-center font-mono text-slate-400">{r.unidadMedida || 'und'}</td>
                            <td className="py-2 px-2 text-right font-mono font-bold text-rose-400">{r.cantidadRechazada}</td>
                            <td className="py-2 px-3 text-slate-300">{r.motivo}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* 4. Completados al 100% en Fletes Previos (PLAN-055 — Desplegable) */}
              {(selectedDetail.itemsCompletadosPrevios?.length || 0) > 0 && (
                <div className="rounded-xl border border-sky-500/30 bg-sky-950/10 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowPrevCompleted(prev => !prev)}
                    className="w-full px-3.5 py-2 bg-sky-950/30 hover:bg-sky-950/40 transition flex items-center justify-between text-xs"
                  >
                    <div className="flex items-center gap-1.5 font-bold text-sky-300">
                      <Package className="w-4 h-4 text-sky-400" />
                      <span>📦 Completados al 100% en fletes anteriores ({selectedDetail.itemsCompletadosPrevios.length} renglones)</span>
                    </div>
                    <span className="text-[11px] underline text-sky-300">
                      {showPrevCompleted ? 'Ocultar' : 'Ver detalle'}
                    </span>
                  </button>
                  {showPrevCompleted && (
                    <div className="overflow-x-auto border-t border-sky-500/20">
                      <table className="w-full text-left text-xs">
                        <thead>
                          <tr className="border-b border-borderSubtle text-[10px] uppercase text-slate-400 bg-surface/60">
                            <th className="py-2 px-3">Insumo</th>
                            <th className="py-2 px-2 text-right">Aprobado</th>
                            <th className="py-2 px-2 text-right text-sky-300">Total Recibido Previo</th>
                            <th className="py-2 px-3">Destino</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-borderSubtle/50">
                          {selectedDetail.itemsCompletadosPrevios.map((cp: any, idx: number) => (
                            <tr key={idx} className="hover:bg-surface/40">
                              <td className="py-2 px-3 font-semibold text-slate-200">{cp.nombre}</td>
                              <td className="py-2 px-2 text-right font-mono text-slate-400">{cp.cantidadAprobada}</td>
                              <td className="py-2 px-2 text-right font-mono font-bold text-sky-300">{cp.totalAcumulado ?? cp.cantidadRecibidaPrevia} ✓</td>
                              <td className="py-2 px-3 text-[11px] text-slate-300">{cp.proyectoNombre || 'Stock General'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* 5. No Aprobados / Cancelados por Gerencia (PLAN-055 — Desplegable) */}
              {(selectedDetail.itemsCanceladosGerencia?.length || 0) > 0 && (
                <div className="rounded-xl border border-slate-700/80 bg-slate-900/40 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowCanceledMgmt(prev => !prev)}
                    className="w-full px-3.5 py-2 bg-slate-900/60 hover:bg-slate-800/60 transition flex items-center justify-between text-xs"
                  >
                    <div className="flex items-center gap-1.5 font-bold text-slate-300">
                      <AlertOctagon className="w-4 h-4 text-rose-400/80" />
                      <span>🚫 No aprobados por Gerencia / Excluidos de compra ({selectedDetail.itemsCanceladosGerencia.length} renglones)</span>
                    </div>
                    <span className="text-[11px] underline text-slate-400">
                      {showCanceledMgmt ? 'Ocultar' : 'Ver detalle'}
                    </span>
                  </button>
                  {showCanceledMgmt && (
                    <div className="overflow-x-auto border-t border-slate-700/60">
                      <table className="w-full text-left text-xs">
                        <thead>
                          <tr className="border-b border-borderSubtle text-[10px] uppercase text-slate-400 bg-surface/60">
                            <th className="py-2 px-3">Insumo Solicitado</th>
                            <th className="py-2 px-2 text-right">Solicitado</th>
                            <th className="py-2 px-2 text-right">Aprobado</th>
                            <th className="py-2 px-3">Estado / Disposición Gerencial</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-borderSubtle/50">
                          {selectedDetail.itemsCanceladosGerencia.map((xc: any, idx: number) => (
                            <tr key={idx} className="hover:bg-surface/40">
                              <td className="py-2 px-3 text-slate-400 line-through">{xc.nombre}</td>
                              <td className="py-2 px-2 text-right font-mono text-slate-400">{xc.cantidadSolicitada ?? 0}</td>
                              <td className="py-2 px-2 text-right font-mono font-bold text-rose-300/90">0</td>
                              <td className="py-2 px-3 text-[11px] text-slate-400">{xc.motivo || xc.estadoFlujo || 'Cancelada'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Panel Semiautomático de Notificación Multicanal Etapa 4 (Telegram Tópico 146 + WhatsApp SANESCA ALMACEN Y DEPOSITO) */}
              <OABStageNotifyPanel
                stage="RAMP_RECEPTION"
                folio={selectedDetail.folioOAB}
                oabId={selectedDetail.oabId}
                storesSummary={selectedDetail.tiendaDestino || '📦 Stock General (MTS)'}
                linesCount={(selectedDetail.itemsRecibidos?.length || 0) + (selectedDetail.itemsFaltantes?.length || 0)}
                operatorName={selectedDetail.receptorNombre || 'Operador de Rampa'}
                operatorRole="Almacén y Rampa"
                vendorName={selectedDetail.proveedor}
                quoteNumber={selectedDetail.numeroNotaEntrega}
                rampSummary={{
                  nriFolio: selectedDetail.nriFolio,
                  actaRechazoFolio: selectedDetail.actaRechazoFolio,
                  proveedor: selectedDetail.proveedor,
                  numeroNotaEntrega: selectedDetail.numeroNotaEntrega,
                  choferNombre: selectedDetail.choferNombre || undefined,
                  placaVehiculo: selectedDetail.placaVehiculo || undefined,
                  nriPrinted: Boolean(printedNriMap[selectedDetail.nriFolio]),
                  rejectionPrinted: Boolean(printedRejectionMap[selectedDetail.nriFolio]),
                  arrivedItems: (selectedDetail.itemsRecibidos || []).map((i: any) => ({
                    codigo: i.codigo || i.sku || undefined,
                    nombre: i.nombre,
                    cantidad: i.cantidadRecibidaHoy,
                    unidad: i.unidadMedida || 'und',
                    ubicacion: i.proyectoNombre
                  })),
                  missingItems: (selectedDetail.itemsFaltantes || []).map((m: any) => ({
                    codigo: m.codigo || m.sku || undefined,
                    nombre: m.nombre,
                    cantidadFaltante: m.saldoPendiente,
                    unidad: m.unidadMedida || 'und'
                  })),
                  rejectedItems: (selectedDetail.itemsRechazados || []).map((r: any) => ({
                    codigo: r.codigo || r.sku || undefined,
                    nombre: r.nombre,
                    cantidadRechazada: r.cantidadRechazada,
                    unidad: r.unidadMedida || 'und',
                    motivo: r.motivo
                  }))
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
